import { useState, useEffect, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Typography } from '@mui/material';

import MeasurementStore from '../../../stores/meas-store';
import { Measurement } from '../../../http/meas-service';
import TimeChart, { HoverPoint, MeasurePoint } from './TimeChart';
import DepthChart from './DepthChart';
import Selector from './Selector';
import { Selection } from '../selection';
import { toDepth } from '../../../utils/depth';

interface Props {
    store: MeasurementStore;
    selected: Selection;
    onSelect: (sel: Selection) => void;
}

function formatMeasure(date: Date): string {
    return new Date(date).toISOString().slice(0, 19).replace('T', ' ');
}

function formatDateTime(date: Date): string {
    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}

// Produces the same local-time label string as utcToLocal() inside TimeChart
function measureLabel(m: Measurement): string {
    const utcStr = formatMeasure(new Date(m.measure));
    const d = new Date(utcStr.replace(' ', 'T') + 'Z');
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function toTimePoints(measurements: Measurement[]): MeasurePoint[] {
    const result: MeasurePoint[] = [];
    for (let i = 0; i < measurements.length; i++) {
        const m = measurements[i];
        result.push({
            measure: formatMeasure(new Date(m.measure)),
            depth: m.depths.map((_, idx) => toDepth(m, idx)),
            values: m.values,
        });
        // Break the line when sensor changes by inserting a gap entry with no values
        if (i < measurements.length - 1 && measurements[i + 1].sensor !== m.sensor) {
            const gapDate = new Date(m.measure);
            gapDate.setSeconds(gapDate.getSeconds() + 1);
            result.push({ measure: formatMeasure(gapDate), depth: [], values: [] });
        }
    }
    return result;
}

//=================================================================================================
export default observer(function Charts({ store, selected, onSelect }: Props) {
    const measurements = store.measurements;
    const [highlightIndex, setHighlightIndex] = useState<number | undefined>(undefined);
    const [timeHoverPoint, setTimeHoverPoint] = useState<HoverPoint[]>([]);
    const [hoveredMeas, setHoveredMeas] = useState<Measurement | undefined>(
        measurements.length > 0 ? measurements[measurements.length - 1] : undefined,
    );

    useEffect(() => {
        const last = measurements.length > 0 ? measurements[measurements.length - 1] : undefined;
        setHoveredMeas(last);
        setTimeHoverPoint(last ? last.values.map((temp, i) => ({ depth: toDepth(last, i), temp })) : []);
    }, [measurements]);

    const timeData = useMemo(() => toTimePoints(measurements), [measurements]);

    const handleTimeHover = useCallback(
        (points: HoverPoint[], label: string) => {
            setTimeHoverPoint(points);
            const meas = measurements.find((m) => measureLabel(m) === label);
            if (meas) setHoveredMeas(meas);
        },
        [measurements],
    );

    const handleDepthHover = useCallback((idx: number | null) => {
        setHighlightIndex(idx ?? undefined);
    }, []);

    const depthName = hoveredMeas ? `Коса-${hoveredMeas.sensor}` : '';
    const depthTime = hoveredMeas ? new Date(hoveredMeas.measure) : new Date();

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                minHeight: 0,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default',
                overflow: 'hidden',
                m: 1,
            }}
        >
            {/* Строка заголовков */}
            <Box sx={{ display: 'flex' }}>
                <Box sx={{ flex: '3 1 0%', minWidth: 0, display: 'flex', alignItems: 'center', pl: 1, pt: 1 }}>
                    <Selector selected={selected} onSelect={onSelect} />
                </Box>
                <Box sx={{ flex: '1 1 0%', minWidth: 0 }}>
                    <Box sx={{ mr: 1, mt: 1, textAlign: 'right' }}>
                        <Typography variant="subtitle2" fontWeight="bold" lineHeight={1.3}>
                            {depthName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {formatDateTime(depthTime)}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Строка графиков */}
            <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>
                <Box sx={{ flex: '3 1 0%', minWidth: 0, minHeight: 0 }}>
                    <TimeChart
                        data={timeData}
                        highlightIndex={highlightIndex}
                        onHover={handleTimeHover}
                        height="100%"
                    />
                </Box>
                <Box sx={{ flex: '1 1 0%', minWidth: 0, minHeight: 0 }}>
                    <DepthChart points={timeHoverPoint} onDepthHover={handleDepthHover} height="100%" />
                </Box>
            </Box>
        </Box>
    );
});
